import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import { AdDetails, GetPopularRequestsResponse } from '@/api/types/ads.types';
import {
  CategoryBaseInfo,
  GetRootCategoriesResponse,
} from '@/api/types/categories.types';
import { GetRegionsResponse } from '@/api/types/regions.types';

interface ServerDataContextProps {
  initialAdDetails: AdDetails | null;
  popularRequests: GetPopularRequestsResponse | null;
  rootCategories: GetRootCategoriesResponse | null;
  currentCategory: CategoryBaseInfo | null;
  regions: GetRegionsResponse | null;
  h1: string | null;
}

const ServerDataContext = createContext<ServerDataContextProps | undefined>(
  undefined
);

export const ServerDataProvider = ({
  children,
  adDetails,
  popularRequests,
  rootCategories,
  currentCategory,
  regions,
  h1,
}: {
  children: ReactNode;
  adDetails?: AdDetails | null;
  popularRequests?: GetPopularRequestsResponse | null;
  rootCategories?: GetRootCategoriesResponse | null;
  currentCategory?: CategoryBaseInfo | null;
  regions?: GetRegionsResponse | null;
  h1?: string | null;
}) => {
  const [resettableData, setResettableData] = useState({
    regions: regions ?? null,
    popularRequests: popularRequests ?? null,
    rootCategories: rootCategories ?? null,
    currentCategory: currentCategory ?? null,
  });

  // Reset all unused data on the client side
  useEffect(() => {
    setResettableData({
      regions: null,
      popularRequests: null,
      rootCategories: null,
      currentCategory: null,
    });
  }, []);

  return (
    <ServerDataContext.Provider
      value={{
        initialAdDetails: adDetails ?? null,
        h1: h1 ?? null,
        ...resettableData,
      }}
    >
      {children}
    </ServerDataContext.Provider>
  );
};

export const useServerDataContext = () => {
  const context = useContext(ServerDataContext);
  if (!context) {
    throw new Error(
      'useServerDataContext must be used within ServerDataProvider'
    );
  }
  return context;
};
